
/** XeAgents.ai**/
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { agents, tabsConfig } from "./useCaseData";
import { useScrollBasedTabs, useResponsive, useStickyTabs } from "./useScrollBasedTabs";
import { AgentCard } from "./AgentCard";
import { TabButton } from "./TabButton";

export function UseCaseTabs() {
  const { t } = useTranslation("landing");
  const isMobile = useResponsive();
  const sticky = useStickyTabs();
  const { activeTab, sectionRef, handleTabClick } = useScrollBasedTabs({
    tabsConfig,
    isMobile: !sticky,
    defaultTab: "inbound",
  });

  const currentTab =
    tabsConfig.find((tab) => tab.id === activeTab) || tabsConfig[0];
  const currentAgents = currentTab.agentIds.map((id) => ({
    ...agents[id],
    title: t(`useCases.agents.${id}.title`),
    description: t(`useCases.agents.${id}.description`),
    categories: agents[id].categories.map((key) => t(`useCases.categories.${key}`)),
  }));
  const displayedAgents = isMobile ? currentAgents.slice(0, 3) : currentAgents;

  return (
    <section
      ref={sectionRef}
      id="use-cases"
      className={`relative bg-gradient-to-b from-white via-slate-50/30 to-white dark:from-slate-950 dark:via-slate-900/30 dark:to-slate-950 ${sticky ? "h-[115vh]" : "py-16 md:py-20"}`}
      data-testid="use-cases-section"
    >
      {/* Background decorations */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(139,92,246,0.06),transparent)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(139,92,246,0.12),transparent)] pointer-events-none" />
      <div className="absolute top-40 -left-20 w-80 h-80 bg-violet-200/15 dark:bg-violet-800/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 -right-20 w-80 h-80 bg-indigo-200/15 dark:bg-indigo-800/10 rounded-full blur-3xl pointer-events-none" />

      <div
        className={
          sticky
            ? "sticky top-0 h-screen flex flex-col justify-center py-12 md:py-16"
            : "py-8"
        }
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 md:mb-14"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-50 dark:bg-violet-950/50 border border-violet-100 dark:border-violet-800/50 mb-6"
            >
              <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-400" />
              <span className="text-sm font-medium text-violet-700 dark:text-violet-300">
                {t("useCases.badge")}
              </span>
            </motion.div>

            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-4 md:mb-6 px-2"
              data-testid="use-cases-headline"
            >
              <span className="text-slate-900 dark:text-white">
                {t("useCases.titleA")}
              </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-indigo-600 dark:from-violet-400 dark:to-indigo-400">
                {t("useCases.titleB")}
              </span>
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-4">
              {t("useCases.body")}
            </p>
          </motion.div>

          <div className="space-y-8 md:space-y-10">
            {/* Tabs */}
            <nav
              role="tablist"
              aria-label={t("useCases.tabsLabel")}
              className="flex justify-center"
              data-testid="use-cases-tabs"
            >
              <div className="inline-flex items-center gap-1 sm:gap-2 p-1.5 rounded-full bg-slate-100/80 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/40 backdrop-blur-sm">
                {tabsConfig.map((tab) => (
                  <TabButton
                    key={tab.id}
                    tab={{ ...tab, label: t(`useCases.tabs.${tab.id}`) }}
                    isActive={activeTab === tab.id}
                    onClick={() => handleTabClick(tab.id)}
                  />
                ))}
              </div>
            </nav>

            {/* Agent Grid */}
            <div className="min-h-0 md:min-h-[600px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  id={`agent-grid-${activeTab}`}
                  role="tabpanel"
                  aria-labelledby={`tab-button-${activeTab}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.35, ease: "easeInOut" }}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6"
                  data-testid={`agent-grid-${activeTab}`}
                >
                  {displayedAgents.map((agent, index) => (
                    <AgentCard key={agent.id} agent={agent} index={index} />
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default UseCaseTabs;